// Visitor analytics with PostHog (EU cloud), per docs/decisions.md: page views, clicks and
// named steps of a visit (stages, cards, timers, choices), so we can see where visitors
// stop and what they do. Patients' words, photos and contact details never go to PostHog:
// - no cookies or storage (everything PostHog keeps lives in memory and is gone on reload),
//   no identify, IP discarded in the project;
// - all text and inputs masked, click text dropped, pictures and videos left out of recordings;
// - only the events in EVENTS, with only their listed properties;
// - never on the staff cockpit.
// Without a project key from the Worker (/api/analytics), nothing loads.

import type { PostHog } from 'posthog-js';

/**
 * The steps of a visit the app reports, and the only properties each may carry. Every
 * value is a name from a fixed list (a stage, a card, an outcome), never free text.
 * The app's language rides on every event (COMMON) so each chart can be split by it.
 */
export const EVENTS = {
  /** kind: first_visit | return_session */
  visit_started: ['kind'],
  /** entry: quick | scan | consult (the start tapped under the greeting, docs/entry-journeys.md) */
  entry_chosen: ['entry'],
  /** stage: the server's stage names (server/agent/visit.ts) */
  stage_reached: ['stage'],
  /** card: body_map | photos | posture | movement | assessment | exercise | sign_in | subscription | quick_question */
  card_shown: ['card'],
  /** outcome: done | skipped | failed */
  card_done: ['card', 'outcome'],
  /** exercise: the exercise or movement id from Remi's pack, never the patient's words */
  exercise_shown: ['exercise'],
  /** action: started | again | paused | resumed | finished; by: companion | patient */
  timer: ['action', 'by'],
  /** choice: subscribed | not_now */
  plan_choice: ['choice'],
  /** item: upgrade | account | scans | settings | language */
  menu: ['item'],
  /**
   * kind: no_reply (nothing reached the companion) | cut_off (the reply broke off) | busy;
   * reason: what failed (network, busy, 503, no stream, reply cut off, error), never words.
   */
  turn_failed: ['kind', 'reason'],
  /** score: 1 to 5; reason: hard_to_follow | too_long | other | none */
  visit_rating: ['score', 'reason'],
  /**
   * Invite friends (shared/referrals.ts). action: link_created | link_shared | link_copied | invite_opened (a friend
   * landed through a link) | sign_in_tapped (an invited guest tapped sign-in before subscribing) | stats_opened (the patient opened their
   * link's stats). Never the code.
   */
  referral: ['action'],
} as const;

/**
 * Properties every listed event may carry: the app's language (sq | en), the release, and
 * `internal` on the team's own devices and test addresses so charts can leave our tests out.
 */
export const COMMON = ['lang', 'app_version', 'internal'] as const;

type EventName = keyof typeof EVENTS;
type Props<E extends EventName> = Partial<Record<(typeof EVENTS)[E][number] | (typeof COMMON)[number], string>>;

// PostHog's own events we keep; anything else is dropped before it leaves the page.
// Rage and dead clicks keep only where on the page they happened; their element text is dropped below.
// Crashes ($exception) keep their type and where in the code they happened; the message is dropped below.
// Heatmaps ($$heatmap) and page speed ($web_vitals) keep only paths, never query strings.
const OWN_EVENTS = new Set(['$pageview', '$pageleave', '$autocapture', '$rageclick', '$dead_click', '$snapshot', '$web_vitals', '$exception', '$$heatmap']);
// Properties that can hold words from the page or the address bar.
const DROP = ['$el_text', '$elements_chain', '$elements', '$el_attr__href', 'title', '$exception_message', '$exception_values', '$exception_personURL', '$exception_source'];
// PostHog's own properties without a `$` that it needs to accept an event.
const SDK = ['token', 'distinct_id'];
// rrweb's meta event, which records the page address in data.href.
const RRWEB_META = 4;
// Where a tap never counts as a dead click. A tap on a text field opens the phone's keyboard,
// which is outside the page: the detector saw no change and reported the message box as dead
// whenever the patient waited before typing. The first two are PostHog's own defaults.
export const DEAD_CLICK_IGNORE = ['.ph-no-deadclick', '.ph-no-capture', 'input', 'textarea'];

let client: PostHog | null = null;
let started = false;
// Steps reported before PostHog has loaded (the page's first seconds); sent once it has.
const pending: { event: EventName; props: Props<EventName> }[] = [];
const PENDING_MAX = 20;
let language: string | null = null;

/** Keeps only the path of an address: visit ids and sign-in codes live in query strings. */
export function stripUrl(url: unknown): unknown {
  if (typeof url !== 'string') return url;
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

/** What PostHog may send: our listed events and properties, PostHog's own ones scrubbed. */
export function scrub<T extends { event: string; properties: Record<string, unknown> } | null>(cr: T): T | null {
  if (!cr) return null;
  const listed = (EVENTS as Record<string, readonly string[]>)[cr.event];
  if (!listed && !OWN_EVENTS.has(cr.event)) return null;
  const properties = { ...cr.properties };
  if (listed) {
    for (const key of Object.keys(properties)) {
      if (!listed.includes(key) && !(COMMON as readonly string[]).includes(key) && !key.startsWith('$') && !SDK.includes(key)) delete properties[key];
    }
  }
  for (const key of DROP) delete properties[key];
  for (const key of Object.keys(properties)) if (key !== '$snapshot_data') properties[key] = stripUrls(properties[key]);
  if (cr.event === '$$heatmap' && properties.$heatmap_data && typeof properties.$heatmap_data === 'object') {
    properties.$heatmap_data = Object.fromEntries(Object.entries(properties.$heatmap_data).map(([url, points]) => [stripUrl(url), points]));
  }
  if (cr.event === '$snapshot' && '$snapshot_data' in properties) properties.$snapshot_data = stripReplayUrls(properties.$snapshot_data);
  if (cr.event === '$exception') properties.$exception_list = scrubExceptions(properties.$exception_list);
  return { ...cr, properties };
}

// Of a stack frame, only where in the code it is: never the source lines around it or its variables.
const FRAME_KEYS = ['filename', 'abs_path', 'function', 'lineno', 'colno', 'in_app', 'platform', 'module'];

/**
 * A crash's list of errors, keeping each one's type, how it was caught and its stack
 * frames' places in the code. The message is emptied: it can quote whatever the code was
 * handling, a patient's words included.
 */
function scrubExceptions(list: unknown): unknown[] {
  if (!Array.isArray(list)) return [];
  return list.map((raw) => {
    const e = (raw ?? {}) as { type?: unknown; mechanism?: unknown; stacktrace?: { type?: unknown; frames?: unknown } };
    const frames = Array.isArray(e.stacktrace?.frames)
      ? e.stacktrace.frames.map((f: Record<string, unknown>) => {
          const out: Record<string, unknown> = {};
          for (const key of FRAME_KEYS) if (f && key in f) out[key] = key === 'filename' || key === 'abs_path' ? stripUrl(f[key]) : f[key];
          return out;
        })
      : undefined;
    return {
      type: typeof e.type === 'string' ? e.type : 'Error',
      value: '',
      ...(e.mechanism && typeof e.mechanism === 'object' ? { mechanism: e.mechanism } : {}),
      ...(frames ? { stacktrace: { type: e.stacktrace?.type ?? 'raw', frames } } : {}),
    };
  });
}

/**
 * Every web address in a property, however deep (page speed nests them in its own
 * objects), down to its path. Replay data is handled by stripReplayUrls.
 */
function stripUrls(value: unknown): unknown {
  if (typeof value === 'string') return /^https?:\/\//.test(value) ? stripUrl(value) : value;
  if (Array.isArray(value)) return value.map(stripUrls);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, stripUrls(v)]));
}

/** Replay records the page address in each meta event; keep only its path. */
function stripReplayUrls(data: unknown): unknown {
  if (Array.isArray(data)) return data.map(stripReplayUrls);
  if (!data || typeof data !== 'object') return data;
  const event = data as { type?: unknown; data?: unknown };
  if (event.type !== RRWEB_META || !event.data || typeof event.data !== 'object' || !('href' in event.data)) return data;
  return { ...event, data: { ...event.data, href: stripUrl((event.data as { href: unknown }).href) } };
}

/** Loads PostHog once, on the patient pages only. Safe to call more than once. */
export async function startAnalytics(): Promise<void> {
  if (started || window.location.pathname.startsWith('/cockpit')) return;
  started = true;
  try {
    const config = (await (await fetch('/api/analytics')).json()) as { key: string | null; host?: string; version?: string | null };
    if (!config.key) return;
    const { default: posthog } = await import('posthog-js');
    posthog.init(config.key, {
      api_host: config.host ?? 'https://eu.i.posthog.com',
      // Memory only: no cookies or storage, so no consent banner, and a new anonymous id on
      // every load. (Cookieless mode would do the same but turns replays off entirely.)
      persistence: 'memory',
      person_profiles: 'never',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      mask_all_text: true,
      mask_all_element_attributes: true,
      disable_surveys: true,
      // Set here rather than left to the project settings, which the page loads at start.
      capture_dead_clicks: { css_selector_ignorelist: DEAD_CLICK_IGNORE },
      capture_heatmaps: true,
      // Page speed only: network timing would record API addresses, which carry visit ids.
      capture_performance: { web_vitals: true, network_timing: false },
      // Crashes in the page go to error tracking (scrubbed below); the console is never
      // recorded in replays, so a stray log line can't carry a patient's words.
      capture_exceptions: true,
      enable_recording_console_log: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskAllElementAttributes: true,
        blockSelector: 'img, video, canvas, picture, svg image',
      },
      before_send: (cr) => scrub(cr),
    });
    if (config.version) posthog.register({ app_version: config.version });
    if (isTeamDevice()) posthog.register({ internal: true });
    if (language) posthog.register({ lang: language });
    client = posthog;
    for (const p of pending.splice(0)) client.capture(p.event, p.props);
  } catch {
    // Analytics never gets in the way of a visit.
  }
}

// Set on a device where a staff member has opened the cockpit: its visits are our own tests.
const TEAM_KEY = 'physiolife.team';

/** Marks this device as the team's (called once the cockpit confirms a staff sign-in). */
export function markTeamDevice(): void {
  try {
    localStorage.setItem(TEAM_KEY, '1');
  } catch {
    // Storage blocked: this device's visits just count as patients'.
  }
}

// The live app's workers.dev address. Every other workers.dev address is a PR preview.
const LIVE_WORKERS_HOST = 'physiolife-app.physiolife.workers.dev';

/**
 * Whether an address only the team opens: a local or LAN dev server, or a PR preview.
 * Unknown addresses count as live, so a clinic's own domain still counts as patients'.
 */
export function isTestHost(hostname: string): boolean {
  if (hostname === LIVE_WORKERS_HOST) return false;
  if (hostname.endsWith('.workers.dev')) return true;
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname.endsWith('.local')) return true;
  if (hostname === '[::1]') return true;
  return /^(127\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname);
}

/** Whether this is the team's device: a staff member has used the cockpit on it, or it opens a test address. */
export function isTeamDevice(): boolean {
  if (isTestHost(window.location.hostname)) return true;
  try {
    return localStorage.getItem(TEAM_KEY) === '1';
  } catch {
    return false;
  }
}

/** The app's language, put on every event from now on. */
export function setLanguage(lang: 'sq' | 'en'): void {
  language = lang;
  client?.register({ lang });
}

/** Reports one step of a visit. Kept until PostHog has loaded; dropped if it never does. */
export function track<E extends EventName>(event: E, props: Props<E> = {}): void {
  if (client) client.capture(event, props);
  else if (pending.length < PENDING_MAX) pending.push({ event, props });
}
